// @ts-nocheck
import {
  WITHDRAW_CIRCUIT_SHA256,
  WITHDRAW_CIRCUIT_WASM,
  WITHDRAW_CIRCUIT_ZKEY,
  resolveNpmCdnCircuitPaths,
} from "./constants.js";

function isBrowserWithCrypto() {
  return (
    typeof window !== "undefined" &&
    typeof fetch === "function" &&
    typeof URL !== "undefined" &&
    typeof Blob !== "undefined" &&
    typeof crypto !== "undefined" &&
    !!crypto.subtle
  );
}

function bytesToHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(data) {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return bytesToHex(new Uint8Array(digest));
}

async function fetchAndVerify(url, expectedHash, label) {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Unable to load ${label} (${response.status}) from ${url}`);
  }

  const buffer = await response.arrayBuffer();
  const actualHash = await sha256Hex(buffer);
  if (actualHash !== expectedHash) {
    throw new Error(
      `${label} integrity mismatch: expected ${expectedHash}, got ${actualHash}`
    );
  }

  return buffer;
}

function toObjectUrlPair(wasmBuffer, zkeyBuffer) {
  const wasmUrl = URL.createObjectURL(
    new Blob([wasmBuffer], { type: "application/wasm" })
  );
  const zkeyUrl = URL.createObjectURL(
    new Blob([zkeyBuffer], { type: "application/octet-stream" })
  );
  return {
    wasmPath: wasmUrl,
    zkeyPath: zkeyUrl,
    cleanup: () => {
      URL.revokeObjectURL(wasmUrl);
      URL.revokeObjectURL(zkeyUrl);
    },
  };
}

async function verifyPair(wasmPath, zkeyPath) {
  const [wasmBuffer, zkeyBuffer] = await Promise.all([
    fetchAndVerify(wasmPath, WITHDRAW_CIRCUIT_SHA256.wasm, WITHDRAW_CIRCUIT_WASM),
    fetchAndVerify(zkeyPath, WITHDRAW_CIRCUIT_SHA256.zkey, WITHDRAW_CIRCUIT_ZKEY),
  ]);
  return toObjectUrlPair(wasmBuffer, zkeyBuffer);
}

/**
 * Resolve browser circuit paths with SHA-256 verification before use.
 *
 * Verifies the requested paths first; on any failure (network, 404, or hash
 * mismatch) it falls back to the pinned, domain-independent jsDelivr CDN. The
 * fallback is safe because every artifact is integrity-checked and proving is
 * aborted (fail closed) if no source yields the expected bytes.
 */
export async function resolveVerifiedCircuitArtifactPaths(wasmPath, zkeyPath) {
  if (!isBrowserWithCrypto()) {
    return {
      wasmPath,
      zkeyPath,
      cleanup: null,
    };
  }

  try {
    return await verifyPair(wasmPath, zkeyPath);
  } catch (primaryError) {
    const cdn = resolveNpmCdnCircuitPaths();
    const primaryWasCdn = wasmPath === cdn.wasm && zkeyPath === cdn.zkey;
    if (primaryWasCdn) {
      throw new Error(
        "arcane-privacy-sdk: unable to load trusted withdraw circuits from the CDN.",
        { cause: primaryError }
      );
    }

    try {
      return await verifyPair(cdn.wasm, cdn.zkey);
    } catch (fallbackError) {
      throw new Error(
        "arcane-privacy-sdk: unable to load trusted withdraw circuits from the requested source or the CDN fallback.",
        { cause: fallbackError }
      );
    }
  }
}
